/**
 * The methodology's calendar rules, as the group form shows them (PR 8a).
 *
 * The server decides (`/api/admin/group-calendar/check`, and again on save):
 * forbidden days, 20:00, hours per day after the school calendar, the same
 * trainer twice, 25 seats, the accreditation period. What is here only
 * arranges its answer for the screen: which row a problem belongs to,
 * whether anything blocks the save, and how the settings read.
 */

export const PERIOD_KINDS = [
  { value: "vacation", label: "Vacanță școlară", hint: "până la 8 ore pe zi" },
  { value: "easter", label: "Vacanța de Paște", hint: "fără activități de formare" },
  { value: "flexible", label: "Interval ales de fiecare județ", hint: "se socotește perioadă de cursuri (4 ore)" },
];

export function kindLabel(kind) {
  return PERIOD_KINDS.find((k) => k.value === kind)?.label || kind;
}

/** `{ index: [problem] }` for the rows, and the rest for under the list. */
export function splitProblems(problems) {
  const byRow = {};
  const group = [];
  (problems || []).forEach((p) => {
    if (p.index === null || p.index === undefined) group.push(p);
    else (byRow[p.index] = byRow[p.index] || []).push(p);
  });
  return { byRow, group };
}

export function blockingCount(problems) {
  return (problems || []).filter((p) => p.blocking).length;
}

/** What the check endpoint is sent: only what the rules read. */
export function checkPayload({ sessions, groupId, trainerId, maxSeats, regime }) {
  const seats = Number(maxSeats);
  return {
    // A Curs FPED cohort is not held to the ministry's calendar (12.10.2026);
    // sent only then, so every other check asks what it always asked.
    ...(regime === "fped" ? { regime } : {}),
    group_id: groupId || null,
    trainer_id: trainerId || null,
    max_seats: Number.isFinite(seats) && seats > 0 ? seats : null,
    sessions: (sessions || []).map((s) => {
      const row = { date: s.date || "", start_time: s.start_time || "", end_time: s.end_time || "" };
      // A session's own split, only when both halves are written (PR 8b).
      if (hasOwnSplit(s)) {
        row.theory_hours = Number(String(s.theory_hours).replace(",", "."));
        row.practice_hours = Number(String(s.practice_hours).replace(",", "."));
      }
      return row;
    }),
  };
}

export function hasOwnSplit(s) {
  return ![s?.theory_hours, s?.practice_hours].some((v) => v === undefined || v === null || v === "");
}

/** A session's own split that cannot be: it must add up to the session's length. */
export function splitProblem(s, minutes) {
  const written = [s?.theory_hours, s?.practice_hours].filter((v) => v !== undefined && v !== null && v !== "").length;
  if (written === 1) return "Completează și cealaltă jumătate, sau lasă-le goale pe amândouă.";
  if (!hasOwnSplit(s) || !minutes) return "";
  const t = Number(String(s.theory_hours).replace(",", "."));
  const p = Number(String(s.practice_hours).replace(",", "."));
  if (Number.isNaN(t) || Number.isNaN(p) || t < 0 || p < 0) return "Orele teoretice și practice sunt numere pozitive.";
  if (Math.abs(t + p - minutes / 60) > 0.01) return `Teoretic + practic trebuie să dea ${String(minutes / 60).replace(".", ",")} h.`;
  return "";
}

/** A stable key, so the form asks again only when something the rules read changed. */
export function checkKey(payload) {
  return JSON.stringify(payload);
}

export function reportLine(count) {
  if (!count) return "";
  return count === 1
    ? "O grupă are sesiuni care nu respectă regulile de calendar."
    : `${count} grupe au sesiuni care nu respectă regulile de calendar.`;
}

function parseDay(iso) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso || "")) return null;
  const d = new Date(`${iso}T12:00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function shortDay(iso) {
  const d = parseDay(iso);
  return d ? d.toLocaleDateString("ro-RO", { day: "2-digit", month: "2-digit", year: "numeric" }) : iso || "";
}

/** The last day a group's last session may fall on, so the final evaluation
 *  (up to `days` later) stays inside the accreditation. */
export function lastSessionDay(accreditationEnd, days = 30) {
  const d = parseDay(accreditationEnd);
  if (!d) return "";
  d.setDate(d.getDate() - days);
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

export const emptyYear = () => ({
  label: "", source: "", start: "", end: "", periods: [], days_off: [], bridges: [],
});
